import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeftOutlined,
  MenuOutlined,
  CloseOutlined,
  GlobalOutlined,
  QrcodeOutlined,
  UnorderedListOutlined,
  MoonFilled,
  SunFilled,
  IdcardOutlined,
} from "@ant-design/icons";
import { Badge, Button, Drawer, Modal, Result, Select, Switch, theme } from "antd";
import { useState } from "react";
import { useApolloClient, useQuery } from "@apollo/client";
import { CHECK_QR_BINDING, GET_USER_EVENTS } from "../graphql/queries";
import { useAppSettings } from "../context/AppSettingsContext";
import PlaceOverview from "./PlaceOverview";
import QRScanner from "./QRScanner";
import QrAssignmentModal from "./QrAssignmentModal";
import IdentifierQrModal from "./IdentifierQrModal";
import { Event, Place } from "../types";
import { useAppTheme } from "../theme/ThemeProvider";
import { useTaskInbox } from "../hooks/useTaskInbox";
interface HeaderProps {
  isOneInventory: boolean;
  placeDetail: Place | null;
  fetchDetail: () => void;
  setPlaceDetail: (place: Place | null) => void;
  location: { latitude: number; longitude: number } | null;
  setLocation: (location: { latitude: number; longitude: number } | null) => void;
  placesForMap: { id: string; name: string }[];
  onLogout: () => void;
}

export default function Header({
  isOneInventory,
  placeDetail,
  fetchDetail,
  setPlaceDetail,
  location,
  setLocation,
  placesForMap,
  onLogout,
}: HeaderProps) {
  const { token } = theme.useToken();
  const apollo = useApolloClient();
  const navigate = useNavigate();
  const locationPage = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isQRScannerOpen, setIsQRScannerOpen] = useState(false);
  const [qrAssignmentModalOpen, setQrAssignmentModalOpen] = useState(false);
  const [assignmentQrId, setAssignmentQrId] = useState("");
  const [bindingConflictOpen, setBindingConflictOpen] = useState(false);
  const [userIdentifierOpen, setUserIdentifierOpen] = useState(false);
  const [placeCodeOpen, setPlaceCodeOpen] = useState(false);
  const { themeName, toggleTheme } = useAppTheme();
  const { appConfig } = useAppSettings();
  const logoUrl = typeof appConfig?.logo === "string" ? appConfig.logo : null;
  const userId = localStorage.getItem("userId") || "";
  const userDisplayName =
    localStorage.getItem("userName") || localStorage.getItem("userLoginName") || "";
  const isGuest = !userId;

  const { badgeCount, banner, dismiss, openTask } = useTaskInbox();

  const { data: eventsData } = useQuery<{ getUserEvents: { events: Event[]; success: boolean } }>(
    GET_USER_EVENTS,
    { variables: { input: { userId } }, skip: !userId }
  );

  const events = eventsData?.getUserEvents?.events || [];
  const savedEventId = localStorage.getItem("eventId") || undefined;
  const currentEventId =
    events.length > 0 && savedEventId && events.some((e) => e.id === savedEventId)
      ? savedEventId
      : undefined;

  const handleEventChange = (eventId: string) => {
    localStorage.setItem("eventId", eventId);
    setPlaceDetail(null);
    setIsModalOpen(false);
    navigate("/");
  };

  if (locationPage.pathname === "/login") return null;

  const showLogoInHeader = locationPage.pathname === "/" || locationPage.pathname === "/map";
  const showPlaceNameInHeader =
    Boolean(placeDetail?.name) &&
    (locationPage.pathname.startsWith("/place/") || locationPage.pathname === "/questions");

  const isPlaceDetailPage = locationPage.pathname.startsWith("/place/");
  const isAtPlacePage = isPlaceDetailPage || locationPage.pathname === "/questions";
  const disableNavigation = isGuest && isPlaceDetailPage;
  const placeIdFromRoute = isPlaceDetailPage
    ? locationPage.pathname.match(/^\/place\/([^/]+)/)?.[1] ?? ""
    : "";
  const effectivePlaceId = placeDetail?.id ?? placeIdFromRoute;
  const effectivePlaceName = placeDetail?.name ?? "—";
  const showPlaceCodeButton = isAtPlacePage && Boolean(effectivePlaceId);
  const eventIdForAssignment =
    currentEventId ?? savedEventId ?? localStorage.getItem("eventId") ?? "";

  const handleBack = () => {
    if (disableNavigation) return;
    if (isOneInventory) {
      setPlaceDetail(null);
      navigate("/");
    } else {
      navigate(-1);
    }
  };

  const handleLogout = () => {
    onLogout();
    setIsModalOpen(false);
    setPlaceDetail(null);
    navigate("/login", { replace: true });
  };

  const openAssignmentModal = (qrId: string) => {
    setAssignmentQrId(qrId);
    setQrAssignmentModalOpen(true);
  };

  const handleQrIdScan = async (qrId: string) => {
    setIsQRScannerOpen(false);
    setIsModalOpen(false);
    try {
      const { data } = await apollo.query<{
        checkQrBinding: {
          bound: boolean;
          message: string;
          id?: string | null;
          tenant?: string | null;
        };
      }>({
        query: CHECK_QR_BINDING,
        variables: { input: { qrId } },
        fetchPolicy: "no-cache",
      });

      const resp = data?.checkQrBinding;
      if (resp?.bound) {
        setAssignmentQrId(qrId);
        setBindingConflictOpen(true);
        return;
      }

      openAssignmentModal(qrId);
    } catch (e) {
      console.error("checkQrBinding error:", e);
      // Pokud kontrola selže, necháme uživatele pokračovat ručně.
      openAssignmentModal(qrId);
    }
  };

  const goToHome = () => {
    if (isGuest) return;
    setPlaceDetail(null);
    navigate("/");
  };

  return (
    <>
      <div
        style={{
          padding: "12px 16px",
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {showLogoInHeader ? (
          <Switch
            checked={themeName === "dark"}
            onChange={toggleTheme}
            checkedChildren={<MoonFilled />}
            unCheckedChildren={<SunFilled />}
          />
        ) : !isGuest && !disableNavigation && locationPage.pathname !== "/" ? (
          <ArrowLeftOutlined onClick={handleBack} style={{ fontSize: 20 }} />
        ) : (
          <div style={{ width: 20 }} />
        )}
        {showLogoInHeader && logoUrl ? (
          <img
            onClick={!isGuest ? goToHome : undefined}
            src={logoUrl}
            alt="logo"
            style={{ height: 30, cursor: isGuest ? "default" : "pointer" }}
          />
        ) : showPlaceNameInHeader ? (
          <div
            style={{
              flex: 1,
              textAlign: "left",
              marginLeft: 12,
              marginRight: 12,
              fontSize: 20,
              fontWeight: 700,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={placeDetail?.name}
          >
            {placeDetail?.name}
          </div>
        ) : (
          <div style={{ width: 20 }} />
        )}

        {!isGuest ? (
          <>
            <Drawer
              title="Menu"
              placement="right"
              closable={false}
              extra={
                <Button type="link" onClick={() => setIsModalOpen(false)}>
                  <CloseOutlined style={{ fontSize: 20, color: token.colorText }} />
                </Button>
              }
              onClose={() => setIsModalOpen(false)}
              open={isModalOpen}
              key="right"
            >
              <div
                style={{ display: "flex", flexDirection: "column", height: "100%", gap: 12 }}
              >
                <div>
                  <div style={{ marginBottom: 6, fontWeight: 500 }}>Akce / Event</div>
                  <Select
                    value={currentEventId}
                    onChange={handleEventChange}
                    options={events.map((e) => ({ label: e.name, value: e.id }))}
                    placeholder="Vyberte akci"
                    style={{ width: "100%" }}
                    size="large"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    setUserIdentifierOpen(true);
                  }}
                  style={{
                    width: "100%",
                    border: `1px solid ${token.colorBorderSecondary}`,
                    borderRadius: 10,
                    padding: "12px 10px",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 10,
                    cursor: "pointer",
                    background: "transparent",
                    textAlign: "left",
                  }}
                >
                  <IdcardOutlined style={{ fontSize: 22, color: token.colorPrimary, flexShrink: 0 }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.25 }}>Můj identifikátor</div>
                    {userDisplayName ? (
                      <div
                        style={{
                          fontSize: 11,
                          color: token.colorTextSecondary,
                          marginTop: 2,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {userDisplayName}
                      </div>
                    ) : null}
                  </div>
                </button>
                {placeDetail && (locationPage.pathname.startsWith("/place/") || locationPage.pathname === "/questions") && (
                  <PlaceOverview
                    placeId={placeDetail.id}
                    placeName={placeDetail.name}
                    fetchPlacesAgain={() => { fetchDetail(); setIsModalOpen(false); }}
                    buttonType="small"
                    location={location}
                    showEvenWithLocation
                  />
                )}
                <Button style={{ width: '100%', height: '50px', fontSize: '18px', marginTop: '10px' }} onClick={() => { setIsModalOpen(false); navigate("/map") }}>
                  <GlobalOutlined />{locationPage.pathname === "/" ? "Mapa" : "Zobrazit na mapě"}
                </Button>
                <Button
                  style={{ width: "100%", height: "50px", fontSize: "18px", marginTop: "10px", position: "relative" }}
                  onClick={() => {
                    setIsModalOpen(false);
                    navigate("/tasks");
                  }}
                >
                  <UnorderedListOutlined /> Úkoly
                  {badgeCount > 0 && (
                    <span style={{
                      position: "absolute",
                      top: 6,
                      right: 10,
                      background: token.colorError,
                      color: "#fff",
                      borderRadius: 20,
                      fontSize: 11,
                      fontWeight: 700,
                      lineHeight: 1,
                      padding: "2px 6px",
                      minWidth: 18,
                      textAlign: "center",
                    }}>
                      {badgeCount > 9 ? "9+" : badgeCount}
                    </span>
                  )}
                </Button>
                {showPlaceCodeButton ? (
                  <Button
                    style={{ width: "100%", height: "50px", fontSize: "18px", marginTop: "10px" }}
                    onClick={() => {
                      setIsModalOpen(false);
                      setPlaceCodeOpen(true);
                    }}
                  >
                    <QrcodeOutlined /> Kód místa
                  </Button>
                ) : null}
                {isPlaceDetailPage ? (
                  <Button
                    style={{ width: "100%", height: "50px", fontSize: "18px", marginTop: "10px" }}
                    onClick={() => {
                      setIsQRScannerOpen(true);
                      setIsModalOpen(false);
                    }}
                  >
                    <QrcodeOutlined /> Nastavit QR
                  </Button>
                ) : null}
                <div style={{ marginTop: "auto" }}>
                  <Button
                    style={{
                      width: "100%",
                      height: "50px",
                      fontSize: "18px",
                    }}
                    type="primary"
                    danger
                    onClick={handleLogout}
                  >
                    Odhlásit se
                  </Button>
                </div>
              </div>
            </Drawer>
            <Badge count={badgeCount > 9 ? "9+" : badgeCount} size="small">
              <MenuOutlined
                onClick={() => setIsModalOpen(true)}
                style={{ fontSize: 24, cursor: "pointer", color: token.colorText }}
              />
            </Badge>
          </>
        ) : (
          <div style={{ width: 24 }} />
        )}
      </div>

      {banner && (
        <div
          style={{
            background: token.colorInfoBg,
            borderBottom: `1px solid ${token.colorInfoBorder}`,
            padding: "8px 16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span style={{ fontSize: 13, color: token.colorInfoText, flex: 1, minWidth: 0 }}>
            {banner.text}
            {banner.key.startsWith("A:") && (
              <>
                {" · "}
                <span
                  onClick={() => openTask(banner)}
                  style={{ cursor: "pointer", textDecoration: "underline", fontWeight: 600 }}
                >
                  Otevřít
                </span>
              </>
            )}
          </span>
          <CloseOutlined
            onClick={() => dismiss(banner)}
            style={{ fontSize: 14, cursor: "pointer", color: token.colorTextSecondary, flexShrink: 0 }}
          />
        </div>
      )}

      <QRScanner
        visible={isQRScannerOpen}
        onClose={() => setIsQRScannerOpen(false)}
        onQrIdScan={handleQrIdScan}
      />
      <Modal
        open={bindingConflictOpen}
        onCancel={() => setBindingConflictOpen(false)}
        footer={null}
      >
        <Result
          status="warning"
          icon={<QrcodeOutlined style={{ color: token.colorPrimary }} />}
          title="QR se už používá na jiném místě"
          subTitle="Chcete ho změnit? Tím se odpojí od původní entity a přiváže se tady."
          extra={[
            <Button key="cancel" danger onClick={() => setBindingConflictOpen(false)}>
              Zrušit
            </Button>,
            <Button
              key="bind"
              type="primary"
              onClick={() => {
                setBindingConflictOpen(false);
                openAssignmentModal(assignmentQrId);
              }}
            >
              Přivázat tady
            </Button>,
          ]}
        />
      </Modal>
      <QrAssignmentModal
        open={qrAssignmentModalOpen && Boolean(assignmentQrId)}
        onClose={() => {
          setQrAssignmentModalOpen(false);
          setAssignmentQrId("");
        }}
        qrId={assignmentQrId}
        placeId={effectivePlaceId}
        placeName={effectivePlaceName}
        eventId={eventIdForAssignment}
      />
      <IdentifierQrModal
        open={userIdentifierOpen}
        onClose={() => setUserIdentifierOpen(false)}
        title="Můj identifikátor"
        payload={{ userId }}
        caption={userDisplayName}
      />
      <IdentifierQrModal
        open={placeCodeOpen}
        onClose={() => setPlaceCodeOpen(false)}
        title="Kód místa"
        payload={{ place: effectivePlaceId }}
        caption={effectivePlaceName}
      />
    </>
  );
}
